import React, { lazy, Suspense, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Alert,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import MemoryIcon from '@mui/icons-material/Memory';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import PluginManifestSummary, { parsePluginManifest } from './PluginManifestSummary';
import PluginLogsPanel from './PluginLogsPanel';
import PluginVersionsPanel from './PluginVersionsPanel';

const PluginCodeEditor = lazy(() => import('./PluginCodeEditor'));

const PluginEditorDialog = ({
  open,
  onClose,
  t,
  selectedFolder,
  pluginName,
  pluginKind,
  pluginType,
  manifestFile,
  scriptFile,
  loadingFiles,
  pluginJson,
  pluginPy,
  onPluginJsonChange,
  onPluginPyChange,
  hasMemory,
  hasChanges = false,
  deleting,
  saving,
  exporting,
  importing,
  reloading,
  canVibeEdit,
  vibeRequest,
  vibeLoading,
  vibePreviewReady,
  onVibeRequestChange,
  onVibeEdit,
  onOpenMemory,
  onOpenDelete,
  onExport,
  onReload,
  onSave,
  pluginLogs = [],
  pluginLogsLoading = false,
  pluginLogsError = '',
  onRefreshPluginLogs,
  pluginVersions = [],
  pluginVersionsLoading = false,
  pluginVersionsError = '',
  restoringVersionId = '',
  onRefreshPluginVersions,
  onRestorePluginVersion,
  showManagementActions = true,
  filesLoaded = true,
}) => {
  const [activeTab, setActiveTab] = useState('json');
  const [rawJsonOpen, setRawJsonOpen] = useState(false);
  const [rawJsonDraft, setRawJsonDraft] = useState('');
  const [rawJsonError, setRawJsonError] = useState('');

  useEffect(() => {
    if (open) {
      setActiveTab('json');
      setRawJsonOpen(false);
      setRawJsonError('');
      setRawJsonDraft('');
    }
  }, [open, selectedFolder]);

  const parsedManifest = parsePluginManifest(pluginJson).manifest;
  const identityLabel = parsedManifest?.name || pluginName || pluginType || selectedFolder;
  const openRawJson = () => {
    setRawJsonDraft(pluginJson || '');
    setRawJsonError('');
    setRawJsonOpen(true);
  };
  const applyRawJson = () => {
    try {
      const rawJsonText = String(rawJsonDraft || '').trim();
      if (!rawJsonText) {
        throw new Error('Plugin manifest cannot be empty.');
      }
      const parsed = JSON.parse(rawJsonText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Plugin manifest must be a JSON object.');
      }
    } catch {
      setRawJsonError(t(
        'global_setting.plugins_raw_invalid_json',
        'Enter a valid JSON object before applying the changes.'
      ));
      return;
    }
    onPluginJsonChange(rawJsonDraft);
    setRawJsonOpen(false);
    setRawJsonError('');
  };
  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: 'calc(100vw - 12px)',
          maxWidth: 'min(1800px, calc(100vw - 12px))',
          height: 'calc(100vh - 12px)',
          maxHeight: 'calc(100vh - 12px)',
          m: '6px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {identityLabel || t('global_setting.plugins_edit_title', 'Plugin')}
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              disabled={saving || deleting || vibeLoading}
              aria-label={t('close', 'Close')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {pluginType || selectedFolder ? (
              <Chip label={pluginType || selectedFolder} size="small" color="primary" />
            ) : null}
            {pluginKind ? <Chip label={pluginKind} size="small" variant="outlined" /> : null}
            {canVibeEdit ? (
              <Chip
                icon={<AutoAwesomeIcon />}
                label={t('global_setting.plugins_vibe_badge', 'AI-editable action')}
                size="small"
                color="info"
                variant="outlined"
              />
            ) : null}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 1, flex: 1, display: 'flex' }}>
        {loadingFiles ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1.25} sx={{ mt: 0.25, width: '100%' }}>
            {canVibeEdit ? (
              <Box
                sx={{
                  p: 1.25,
                  border: '1px solid #bfdbfe',
                  borderRadius: 1,
                  backgroundColor: '#eff6ff'
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {t('global_setting.plugins_vibe_title', 'Vibe edit with AI')}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'global_setting.plugins_vibe_description',
                      'Describe the behavior you want to change. The AI will first read the current plugin and explain what it understands before generating any code.'
                    )}
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={5}
                      size="small"
                      label={t('global_setting.plugins_vibe_request_label', 'What should change?')}
                      placeholder={t(
                        'global_setting.plugins_vibe_request_placeholder',
                        'Example: Validate the license number before calling the API and return a clear error when it is missing.'
                      )}
                      value={vibeRequest || ''}
                      onChange={(event) => onVibeRequestChange(event.target.value)}
                      disabled={vibeLoading}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={vibeLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                      disabled={vibeLoading || !String(vibeRequest || '').trim()}
                      onClick={onVibeEdit}
                      sx={{
                        minWidth: { xs: '100%', md: 170 },
                        minHeight: 40,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {vibeLoading
                        ? t('global_setting.plugins_vibe_generating', 'Reviewing current plugin...')
                        : t('global_setting.plugins_vibe_submit', 'Review with AI')}
                    </Button>
                  </Stack>
                  {vibePreviewReady ? (
                    <Alert severity="success" sx={{ py: 0 }}>
                      {t(
                        'global_setting.plugins_vibe_ready_message',
                        'The AI proposal is loaded in the editor. Review the code, then click Save Plugin to apply it.'
                      )}
                    </Alert>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
            <Tabs
              value={activeTab}
              onChange={(_event, value) => {
                setActiveTab(value);
                if (value === 'logs' && typeof onRefreshPluginLogs === 'function') {
                  onRefreshPluginLogs();
                }
                if (value === 'versions' && typeof onRefreshPluginVersions === 'function') {
                  onRefreshPluginVersions();
                }
              }}
              sx={{ minHeight: 40 }}
            >
              <Tab
                value="json"
                label={t('global_setting.plugins_configuration', 'Configuration')}
              />
              <Tab
                value="python"
                label={scriptFile || t('global_setting.plugins_python_label', 'plugin.py')}
              />
              <Tab
                value="logs"
                label={t('global_setting.plugins_logs_tab', 'LOGS')}
              />
              <Tab
                value="versions"
                label={t('global_setting.plugins_versions_tab', 'VERSIONS')}
              />
            </Tabs>

            {activeTab === 'json' ? (
              <PluginManifestSummary
                pluginJson={pluginJson}
                t={t}
                onEditRaw={openRawJson}
              />
            ) : activeTab === 'python' ? (
              <Box sx={{ border: '1px solid #d0d7de', borderRadius: 1, overflow: 'hidden' }}>
                <Suspense
                  fallback={(
                    <Box
                      sx={{
                        height: canVibeEdit ? 'calc(100vh - 330px)' : 'calc(100vh - 180px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                >
                  <PluginCodeEditor
                    value={pluginPy || ''}
                    height={canVibeEdit ? 'calc(100vh - 330px)' : 'calc(100vh - 180px)'}
                    language="python"
                    onChange={(value) => onPluginPyChange(value)}
                  />
                </Suspense>
              </Box>
            ) : activeTab === 'logs' ? (
              <PluginLogsPanel
                logs={pluginLogs}
                loading={pluginLogsLoading}
                error={pluginLogsError}
                pluginType={pluginType}
                onRefresh={onRefreshPluginLogs}
                t={t}
              />
            ) : (
              <PluginVersionsPanel
                versions={pluginVersions}
                loading={pluginVersionsLoading}
                error={pluginVersionsError}
                onRefresh={onRefreshPluginVersions}
                onRestore={onRestorePluginVersion}
                restoringVersionId={restoringVersionId}
                t={t}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {showManagementActions ? (
          <>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={importing || exporting || reloading || loadingFiles || deleting || saving || vibeLoading}
              onClick={onExport}
            >
              {exporting
                ? t('global_setting.plugins_exporting', 'Exporting...')
                : t('global_setting.plugins_export', 'Export ZIP')}
            </Button>
            <Button
              variant="outlined"
              startIcon={reloading ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={importing || reloading || exporting || loadingFiles || deleting || saving || vibeLoading}
              onClick={onReload}
            >
              {reloading
                ? t('global_setting.plugins_reloading', 'Reloading...')
                : t('global_setting.plugins_reload', 'Reload')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<MemoryIcon />}
              disabled={!hasMemory || importing || loadingFiles || deleting || exporting || reloading || vibeLoading}
              onClick={onOpenMemory}
            >
              {t('global_setting.plugins_view_memory', 'View memory')}
            </Button>
            <Tooltip title={deleting
              ? t('global_setting.plugins_deleting', 'Deleting...')
              : t('global_setting.plugins_delete', 'Delete')}>
              <span>
                <IconButton
                  color="error"
                  disabled={importing || deleting || loadingFiles || saving || exporting || reloading || vibeLoading}
                  onClick={onOpenDelete}
                  aria-label={deleting
                    ? t('global_setting.plugins_deleting', 'Deleting...')
                    : t('global_setting.plugins_delete', 'Delete')}
                >
                  {deleting ? <CircularProgress size={22} /> : <DeleteIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : null}
        <Tooltip title={saving
          ? t('global_setting.plugins_saving', 'Saving...')
          : t('global_setting.plugins_save', 'Save')}>
          <span>
            <IconButton
              color="primary"
              disabled={!filesLoaded || !hasChanges || importing || saving || loadingFiles || deleting || exporting || reloading || vibeLoading}
              onClick={onSave}
              aria-label={saving
                ? t('global_setting.plugins_saving', 'Saving...')
                : t('global_setting.plugins_save', 'Save')}
            >
              {saving ? <CircularProgress size={22} color="inherit" /> : <SaveIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('global_setting.cancel', 'Cancel')}>
          <span>
            <IconButton
              disabled={importing || saving || deleting || exporting || reloading || vibeLoading}
              onClick={onClose}
              aria-label={t('global_setting.cancel', 'Cancel')}
            >
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogActions>
      <Dialog
        open={rawJsonOpen}
        onClose={() => setRawJsonOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <CodeIcon fontSize="small" />
            <span>
              {t('global_setting.plugins_raw_dialog_title', 'Raw plugin configuration')}
            </span>
            <Typography variant="caption" color="text.secondary">
              {manifestFile || t('global_setting.plugins_json_label', 'plugin.json')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              'global_setting.plugins_raw_description',
              'Advanced users can inspect and edit the original JSON data here. Apply the changes, then save the plugin.'
            )}
          </Typography>
          {rawJsonError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {rawJsonError}
            </Alert>
          ) : null}
          <Box sx={{ border: '1px solid #d0d7de', borderRadius: 1, overflow: 'hidden' }}>
            <Suspense
              fallback={(
                <Box
                  sx={{
                    height: 'calc(100vh - 300px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            >
              <PluginCodeEditor
                value={rawJsonDraft}
                height="calc(100vh - 300px)"
                language="json"
                onChange={(value) => {
                  setRawJsonDraft(value);
                  setRawJsonError('');
                }}
              />
            </Suspense>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawJsonOpen(false)}>
            {t('global_setting.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={applyRawJson}>
            {t('global_setting.plugins_apply_raw', 'Apply changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PluginEditorDialog;
